import type { AppInfo, AppTab } from "@/shared/apps";
import { useCan, useModule } from "@/shared/lib/session";
import { modules } from "./modules";

const { accounting, appointments, crm, directory, helpdesk, hr, inventory, invoicing, payroll, pos, projects, realestate, requests } = modules;

/**
 * The menu's sections, in order; label is an i18n key (none for the first: requests sits
 * with the personal pages). Customers and products sit with the apps that sell to them.
 */
export const menuGroups: { label?: string; apps: AppInfo[] }[] = [
  { apps: [requests.app] },
  { label: "nav.selling", apps: [crm.app, invoicing.app, directory.app, pos.app, appointments.app, realestate.app, helpdesk.app] },
  { label: "nav.work", apps: [projects.app, inventory.app] },
  { label: "nav.people", apps: [hr.app, payroll.app] },
  { label: "nav.finance", apps: [accounting.app] },
];

/** Every app, in menu order. */
export const apps: AppInfo[] = menuGroups.flatMap((g) => g.apps);

/** Apps a workspace switches on or off (the directory comes with the apps that need it). */
export const choosable = apps.filter((a) => !a.shownWith);

/** Whether an app is switched on for the workspace. */
export function useAppOn() {
  const mod = useModule();
  return (a: AppInfo) => (a.shownWith ? a.shownWith.some(mod) : mod(a.key));
}

/** The workspace conditions a tab can wait for (AppTab.when). */
function useConditions(): Record<NonNullable<AppTab["when"]>, boolean> {
  const mod = useModule();
  const can = useCan();
  // Only those who may read the warehouses ask how many there are; for the rest it was refused on every page.
  return { manyWarehouses: inventory.useManyWarehouses(mod("inventory") && can("inventory.settings.read")) };
}

/** The apps switched on that the user may open, each with only the tabs they may open and that have something to offer. */
export function useVisibleApps(): AppInfo[] {
  const on = useAppOn();
  const can = useCan();
  const met = useConditions();
  return apps
    .filter(on)
    .map((a) => ({ ...a, tabs: a.tabs.filter((t) => (!t.perm || t.perm.some(can)) && (!t.when || met[t.when])) }))
    .filter((a) => a.tabs.length > 0);
}

/** A link written as "/path?a=1", split into the router's path and search. */
export function hrefParts(href: string): { to: string; search: Record<string, string> } {
  const [to, query = ""] = href.split("?");
  return { to, search: Object.fromEntries(new URLSearchParams(query)) };
}
